import { useState, useEffect } from "react";
import { useParams } from "react-router";
import { toast } from "sonner";
import { format } from "date-fns";
import { actionRegistry, AppointmentActionContext, UserRole, BaseAppointmentAction } from "./actions";
import { appointment_status } from "@/models/appointment.model";
import { uuid } from "@/interfaces";
import type { NoteAudience } from "@shyne/shared/api";
import { fetchParentCategoriesForVendor, fetchChildServices } from "@/app/pmc/appointments/appointments.api";
import { chatStatusChoices } from "@/lib/appointment-go-back";

interface UseAppointmentActionsProps {
  appointmentId: uuid;
  currentStatus: appointment_status;
  currentDate?: string;
  userRole: UserRole;
  vendorId?: uuid;
  serviceId?: uuid;
}

export const useAppointmentActions = ({
  appointmentId,
  currentStatus,
  currentDate,
  userRole,
  vendorId,
  serviceId,
}: UseAppointmentActionsProps) => {
  const { roomId } = useParams();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState<BaseAppointmentAction | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [newDate, setNewDate] = useState<Date | undefined>(undefined);
  const [comment, setComment] = useState<string>("");
  const [reason, setReason] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [commentAudience, setCommentAudience] = useState<NoteAudience[]>([]);

  // Service change specific state
  const [parentCategories, setParentCategories] = useState<string[]>([]);
  const [selectedParent, setSelectedParent] = useState<string>("");
  const [availableServices, setAvailableServices] = useState<{ id: string; name: string }[]>([]);
  const [selectedService, setSelectedService] = useState<string>("");
  const [selectedServiceName, setSelectedServiceName] = useState<string>("");
  const [loadingServices, setLoadingServices] = useState(false);

  const context: AppointmentActionContext = {
    appointmentId,
    currentStatus,
    currentDate,
    userRole,
    vendorId,
    serviceId,
    roomId,
  };

  const availableActions = actionRegistry.getAvailableActions(currentStatus, userRole);

  // Load parent categories when service change dialog opens
  const loadParentCategories = async () => {
    if (!vendorId) {
      console.error("No vendor ID available for loading parent categories");
      return;
    }

    setLoadingServices(true);
    try {
      const categories = await fetchParentCategoriesForVendor(vendorId);
      setParentCategories(categories);
    } catch (error) {
      console.error("Failed to load parent categories:", error);
      setParentCategories([]);
    } finally {
      setLoadingServices(false);
    }
  };

  // Load services when parent is selected
  const loadChildServices = async () => {
    setSelectedService("");
    setAvailableServices([]);

    if (!selectedParent || !vendorId) return;

    setLoadingServices(true);
    try {
      const services = await fetchChildServices(vendorId, selectedParent);
      setAvailableServices(services);
    } catch (error) {
      console.error("Failed to load services:", error);
      setAvailableServices([]);
    } finally {
      setLoadingServices(false);
    }
  };

  // Load parent categories when service change dialog opens
  useEffect(() => {
    if (selectedAction?.config.id === "change_service" && dialogOpen) {
      loadParentCategories();
    }
  }, [selectedAction?.config.id, dialogOpen]);

  // Load services when parent is selected
  useEffect(() => {
    if (
      selectedParent &&
      (selectedAction?.config.id === "change_service" || selectedAction?.config.id === "request_service_change") &&
      vendorId
    ) {
      loadChildServices();
    }
  }, [selectedParent, selectedAction?.config.id, vendorId]);

  // Load parent categories when individual service change dialog opens
  useEffect(() => {
    if (selectedAction?.config.id === "request_service_change" && dialogOpen) {
      loadParentCategories();
    }
  }, [selectedAction?.config.id, dialogOpen]);

  const handleActionClick = async (actionId: string) => {
    const action = actionRegistry.getAction(actionId);

    if (!action) return;

    if (action.config.requiresDialog) {
      // Small delay to allow dropdown to fully close before opening modal
      setTimeout(() => {
        setSelectedAction(action);
        setDialogOpen(true);
      }, 50);
    } else {
      // Execute action immediately for simple actions
      const result = await actionRegistry.executeAction(actionId, context);

      if (result.success) {
        toast.success(result.message || "Action completed successfully");
      } else {
        toast.error(result.message || "Action failed");
      }
    }
  };

  const handleDialogConfirm = async () => {
    if (!selectedAction || submitting) return;

    const payload: any = { roomId };

    switch (selectedAction.config.id) {
      case "reschedule":
      case "reschedule_request":
        if (newDate) {
          payload.newDate = format(newDate, "yyyy-MM-dd");
        }
        break;

      case "add_comment":
        payload.comment = comment;
        payload.audience = context.userRole === UserRole.VENDOR ? commentAudience : undefined;
        break;

      case "cancel_request":
        payload.reason = reason;
        break;

      case "status_change":
        payload.nextStatus = selectedStatus;
        if (selectedStatus === appointment_status.goback && newDate) {
          payload.newDate = format(newDate, "yyyy-MM-dd");
        }
        break;

      case "change_service":
        if (selectedService && selectedServiceName) {
          payload.newServiceId = selectedService;
          payload.newServiceName = selectedServiceName;
          payload.parentCategory = selectedParent;
        }
        break;

      case "request_reassignment":
        payload.reason = reason;
        break;

      case "request_service_change":
        if (selectedService && selectedServiceName) {
          payload.newServiceId = selectedService;
          payload.newServiceName = selectedServiceName;
          payload.parentCategory = selectedParent;
        }
        break;
    }

    setSubmitting(true);
    const result = await actionRegistry.executeAction(selectedAction.config.id, context, payload).finally(() => setSubmitting(false));

    if (result.success) {
      toast.success(result.message || "Action completed successfully");
      closeDialog();
    } else {
      toast.error(result.message || "Action failed");
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelectedAction(null);
    // Ensure pointer events are restored
    document.body.style.pointerEvents = "";
    // Reset form state
    setNewDate(undefined);
    setComment("");
    setReason("");
    setSelectedStatus("");
    setCommentAudience([]);
    setSelectedParent("");
    setSelectedService("");
    setSelectedServiceName("");
  };

  const isConfirmDisabled = () => {
    if (!selectedAction || submitting) return true;

    switch (selectedAction.config.id) {
      case "reschedule":
      case "reschedule_request":
        return !newDate;
      case "add_comment":
        return !comment.trim();
      case "cancel_request":
        return false; // Reason is optional
      case "status_change":
        return !selectedStatus || (selectedStatus === appointment_status.goback && !newDate);
      case "change_service":
        return !selectedService || !selectedServiceName || !selectedParent;
      case "request_reassignment":
        return !reason.trim();
      case "request_service_change":
        return !selectedService || !selectedServiceName || !selectedParent;
      default:
        return false;
    }
  };

  const getConfirmButtonText = () => {
    if (!selectedAction) return "Confirm";

    switch (selectedAction.config.id) {
      case "reschedule_request":
      case "cancel_request":
      case "change_service":
        return "Send Request";
      case "add_comment":
        return "Add Comment";
      case "cancel":
        return "Cancel Appointment";
      case "status_change":
        return "Change Status";
      case "request_reassignment":
        return "Send Request";
      case "request_service_change":
        return "Send Request";
      default:
        return "Confirm";
    }
  };

  const getAvailableStatuses = () => {
    return chatStatusChoices(currentStatus);
  };

  return {
    // State
    availableActions,
    dialogOpen,
    selectedAction,

    // Form state
    newDate,
    setNewDate,
    comment,
    setComment,
    reason,
    setReason,
    selectedStatus,
    setSelectedStatus,
    commentAudience,
    setCommentAudience,

    // Service change state
    parentCategories,
    selectedParent,
    setSelectedParent,
    availableServices,
    selectedService,
    setSelectedService,
    selectedServiceName,
    setSelectedServiceName,
    loadingServices,

    // Actions
    handleActionClick,
    handleDialogConfirm,
    closeDialog,

    // Helpers
    isConfirmDisabled,
    getConfirmButtonText,
    getAvailableStatuses,

    // Context
    context,
  };
};
